import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Card, Empty, Spin, Table } from "antd";
import { DoubleLeftOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
	buildKycCompanyTabUrl,
	buildKycDashboardReturnLink,
	buildKycMaterialOrdersTabReturn,
	buildKycMaterialsTableSearchReturn,
	buildKycPartnerFunctionOrdersTabReturn,
	buildKycPartnerFunctionTabReturn,
	buildKycTableSearchUrl,
	kycBreadcrumbLabelsMatch,
	loadKycOrderNavContext,
	saveKycOrderNavContext,
	formatKycCreatedOnDate,
} from "./kycHelpers";
import { fetchOrderDetailList } from "./kycOrderShared";
import "./KYC-Dashboard.css";
import KycStickyTopBar from "./KycStickyTopBar";

const SERVER_SORTER = { compare: (_a, _b) => 0 };

const getCellValue = (record, keys) => {
	const keyList = Array.isArray(keys) ? keys : [keys];
	for (const key of keyList) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}
	return "-";
};

const parseAmount = (value) => {
	if (value === undefined || value === null || value === "" || value === "-") {
		return null;
	}
	const numeric = Number(String(value).replace(/[^0-9.-]/g, ""));
	return Number.isNaN(numeric) ? null : numeric;
};

const formatAmount = (value) => {
	const numeric = parseAmount(value);
	if (numeric === null) {
		return "-";
	}
	return numeric.toLocaleString("en-IN", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const formatDesignAttach = (value) => {
	if (value === true || String(value).toLowerCase() === "true" || String(value).toLowerCase() === "yes") {
		return "Yes";
	}
	if (value === false || String(value).toLowerCase() === "false" || String(value).toLowerCase() === "no") {
		return "No";
	}
	if (value === undefined || value === null || value === "") {
		return "-";
	}
	return String(value);
};

const formatDateValue = (value) => {
	if (value === undefined || value === null || value === "" || value === "-") {
		return "-";
	}
	return formatKycCreatedOnDate(value);
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}
	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	const o = String(tableSorter.order || "").toUpperCase();
	if (o === "ASC") {
		return "ascend";
	}
	if (o === "DESC") {
		return "descend";
	}
	return null;
};

const tableEmptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data found" />;

const KycOrderDetails = () => {
	const { orderId } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [lineItems, setLineItems] = useState([]);
	const [tableState, setTableState] = useState({
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [tableSorter, setTableSorter] = useState({
		field: "itemNo",
		order: "ASC",
	});

	const resolvedOrderId = useMemo(() => {
		const fromParam = orderId ? String(orderId).trim() : "";
		if (fromParam) {
			return fromParam;
		}
		return location.state?.orderId ? String(location.state.orderId).trim() : "";
	}, [orderId, location.state?.orderId]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [resolvedOrderId]);

	const navContext = useMemo(() => {
		const fromState = location.state || {};
		const stored = loadKycOrderNavContext(resolvedOrderId);
		return {
			crmOrderNumber:
				fromState.crmOrderNumber ||
				stored.crmOrderNumber ||
				fromState.crmOrderNo ||
				stored.crmOrderNo ||
				resolvedOrderId ||
				"Order",
			returnSource: fromState.returnSource || stored.returnSource || "dashboard",
			materialProductName: fromState.materialProductName || stored.materialProductName || "",
			materialName: fromState.materialName || stored.materialName || "",
			materialsCustomer: fromState.materialsCustomer || stored.materialsCustomer || "",
			accountName: fromState.accountName || stored.accountName || "",
			groupCompanyId: fromState.groupCompanyId || stored.groupCompanyId || "",
			groupCompanyName: fromState.groupCompanyName || stored.groupCompanyName || "",
			returnTab: fromState.returnTab || stored.returnTab || "orders",
			partnerCrmGuid: fromState.partnerCrmGuid || stored.partnerCrmGuid || "",
			customer: fromState.customer || stored.customer || "",
		};
	}, [location.state, resolvedOrderId]);

	const isMaterialReturn =
		navContext.returnSource === "material" && Boolean(navContext.materialProductName);
	const isPartnerReturn =
		navContext.returnSource === "partner" && Boolean(navContext.partnerCrmGuid);

	useEffect(() => {
		if (!resolvedOrderId) {
			return;
		}
		saveKycOrderNavContext(resolvedOrderId, {
			crmOrderNumber: navContext.crmOrderNumber,
			returnSource: navContext.returnSource,
			materialProductName: navContext.materialProductName,
			materialName: navContext.materialName,
			materialsCustomer: navContext.materialsCustomer,
			accountName: navContext.accountName,
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: navContext.groupCompanyName,
			returnTab: navContext.returnTab,
			partnerCrmGuid: navContext.partnerCrmGuid,
			customer: navContext.customer,
		});
	}, [resolvedOrderId, navContext]);

	const orderNumberLabel = useMemo(() => {
		const label = navContext.crmOrderNumber;
		if (label && String(label).trim() && String(label).trim() !== "Order") {
			return String(label).trim();
		}
		return resolvedOrderId || "Order";
	}, [navContext.crmOrderNumber, resolvedOrderId]);

	const dashboardOrdersTabLink = buildKycCompanyTabUrl("orders");

	const materialOrdersTabReturn = useMemo(
		() =>
			buildKycMaterialOrdersTabReturn({
				productName: navContext.materialProductName,
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const dashboardMaterialsReturn = useMemo(
		() =>
			buildKycMaterialsTableSearchReturn({
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
			}),
		[navContext]
	);

	const partnerFunctionTabReturn = useMemo(
		() =>
			buildKycPartnerFunctionTabReturn({
				crmGuid: navContext.partnerCrmGuid,
				partnerTab: "partnerFunction",
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				accountName: navContext.accountName,
				materialsCustomer: navContext.materialsCustomer,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const partnerOrdersTabReturn = useMemo(
		() =>
			buildKycPartnerFunctionOrdersTabReturn({
				crmGuid: navContext.partnerCrmGuid,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				accountName: navContext.accountName,
				materialsCustomer: navContext.materialsCustomer,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const groupCompanyBreadcrumbLabel = (navContext.groupCompanyName || "").trim();
	const accountBreadcrumbLabel = (navContext.accountName || "").trim();
	const materialsBreadcrumbLabel = (navContext.materialsCustomer || "").trim();
	const normalizedGroupLabel = groupCompanyBreadcrumbLabel.toLowerCase();
	const normalizedAccountLabel = accountBreadcrumbLabel.toLowerCase();
	const hasDistinctGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel !== normalizedAccountLabel;
	const hasDuplicateGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel === normalizedAccountLabel;
	const showPartnerGroupInBreadcrumb = Boolean(
		groupCompanyBreadcrumbLabel &&
			(navContext.returnTab === "materials" || hasDistinctGroupAndAccount)
	);
	const showPartnerAccountInBreadcrumb = Boolean(
		accountBreadcrumbLabel &&
			navContext.returnTab === "account" &&
			(hasDistinctGroupAndAccount ||
				hasDuplicateGroupAndAccount ||
				!groupCompanyBreadcrumbLabel)
	);
	const showPartnerMaterialsInBreadcrumb =
		navContext.returnTab === "materials" && Boolean(materialsBreadcrumbLabel);
	const groupAndAccountNamesMatch = kycBreadcrumbLabelsMatch(
		groupCompanyBreadcrumbLabel,
		showPartnerAccountInBreadcrumb ? accountBreadcrumbLabel : ""
	);

	const dashboardGroupCompanyLink = useMemo(() => {
		if (!showPartnerGroupInBreadcrumb) {
			return "/kyc";
		}
		return buildKycDashboardReturnLink({
			returnTab: "groupCompany",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
		});
	}, [showPartnerGroupInBreadcrumb, groupCompanyBreadcrumbLabel, navContext.groupCompanyId]);

	const dashboardAccountLink = useMemo(() => {
		if (!showPartnerAccountInBreadcrumb) {
			return buildKycCompanyTabUrl("account");
		}
		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
			accountName: accountBreadcrumbLabel,
		});
	}, [
		showPartnerAccountInBreadcrumb,
		accountBreadcrumbLabel,
		groupCompanyBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const dashboardMaterialsDrillAccountLink = useMemo(() => {
		if (!showPartnerMaterialsInBreadcrumb) {
			return null;
		}
		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: showPartnerGroupInBreadcrumb ? groupCompanyBreadcrumbLabel : "",
			accountName: materialsBreadcrumbLabel,
		});
	}, [
		showPartnerMaterialsInBreadcrumb,
		showPartnerGroupInBreadcrumb,
		groupCompanyBreadcrumbLabel,
		materialsBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const dashboardOrdersBackLink = useMemo(() => {
		if (isPartnerReturn) {
			return partnerOrdersTabReturn.link;
		}
		if (isMaterialReturn) {
			return materialOrdersTabReturn.link;
		}
		const search =
			orderNumberLabel && orderNumberLabel !== "Order" ? String(orderNumberLabel).trim() : "";
		if (search) {
			return buildKycTableSearchUrl(search, { companyTab: "orders" });
		}
		return dashboardOrdersTabLink;
	}, [
		isPartnerReturn,
		isMaterialReturn,
		partnerOrdersTabReturn.link,
		materialOrdersTabReturn.link,
		orderNumberLabel,
		dashboardOrdersTabLink,
	]);

	const dashboardOrdersBackState = useMemo(() => {
		if (isPartnerReturn) {
			return partnerOrdersTabReturn.state;
		}
		if (isMaterialReturn) {
			return materialOrdersTabReturn.state;
		}
		const search =
			orderNumberLabel && orderNumberLabel !== "Order" ? String(orderNumberLabel).trim() : "";
		if (!search) {
			return { returnTab: "orders" };
		}
		return {
			returnTab: "orders",
			crmOrderNumber: search,
			tableSearch: search,
		};
	}, [
		isPartnerReturn,
		isMaterialReturn,
		partnerOrdersTabReturn.state,
		materialOrdersTabReturn.state,
		orderNumberLabel,
	]);

	const materialBreadcrumbLabel =
		navContext.materialName || navContext.materialProductName || "Material";

	const loadOrderDetails = useCallback(async () => {
		if (!resolvedOrderId) {
			setLineItems([]);
			setTableState((prev) => ({ ...prev, totalRecords: 0 }));
			return;
		}

		setIsLoading(true);
		try {
			const result = await fetchOrderDetailList({
				orderId: resolvedOrderId,
				pageindex: tableState.currentPage,
				pagesize: tableState.pageSize,
				sortcolumn: tableSorter.field,
				orderBy: tableSorter.order,
			});
			setLineItems(result.rows);
			setTableState((prev) => ({
				...prev,
				totalRecords: result.totalRecords,
				currentPage: result.currentPage,
			}));
		} catch (error) {
			console.log(error, "Error");
			NotificationManager.error("Unable to load order details.");
			setLineItems([]);
			setTableState((prev) => ({ ...prev, totalRecords: 0 }));
		} finally {
			setIsLoading(false);
		}
	}, [
		resolvedOrderId,
		tableState.currentPage,
		tableState.pageSize,
		tableSorter.field,
		tableSorter.order,
	]);

	useEffect(() => {
		loadOrderDetails();
	}, [loadOrderDetails]);

	const handleTableAscDes = (pagination, filters, sorter) => {
		const s = Array.isArray(sorter) ? sorter[0] || {} : sorter || {};
		const sortOrder = s.order;
		const rawField = s.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: tableSorter.field;

		let nextOrder = tableSorter.order;
		if (sortOrder === "ascend") {
			nextOrder = "ASC";
		} else if (sortOrder === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tableState.currentPage ||
				(pagination.pageSize != null &&
					tableState.pageSize != null &&
					pagination.pageSize !== tableState.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		setTableState((prev) => ({
			...prev,
			currentPage: pagination.current,
			pageSize: pagination.pageSize || prev.pageSize,
		}));
		setTableSorter({
			field: sortField,
			order: nextOrder,
		});
	};

	const lineItemColumns = [
		{
			title: "Design Attach",
			dataIndex: "designAttached",
			key: "designAttached",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "designAttached"),
			render: (_, record) => (
				<span>{formatDesignAttach(record?.designAttached)}</span>
			),
		},
		{
			title: "Item No",
			dataIndex: "itemNo",
			key: "itemNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 120,
			sortOrder: getSortOrder(tableSorter, "itemNo"),
			render: (_, record) => <span>{getCellValue(record, ["itemNo"])}</span>,
		},
		{
			title: "Created Date",
			dataIndex: "createdOn",
			key: "createdOn",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 150,
			sortOrder: getSortOrder(tableSorter, "createdOn"),
			render: (_, record) => formatDateValue(record?.createdOn),
		},
		{
			title: "Name",
			dataIndex: "materialName",
			key: "materialName",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "materialName"),
			render: (_, record) => <span>{getCellValue(record, ["materialName"])}</span>,
		},
		{
			title: "Material Code",
			dataIndex: "materialSAPCode",
			key: "materialSAPCode",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 150,
			sortOrder: getSortOrder(tableSorter, "materialSAPCode"),
			render: (_, record) => <span>{getCellValue(record, ["materialSAPCode"])}</span>,
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 130,
			sortOrder: getSortOrder(tableSorter, "quantity"),
			render: (_, record) => <span>{formatAmount(record?.quantity)}</span>,
		},
		{
			title: "Base Price",
			dataIndex: "baseprice",
			key: "baseprice",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 130,
			sortOrder: getSortOrder(tableSorter, "baseprice"),
			render: (_, record) => <span>{formatAmount(record?.baseprice)}</span>,
		},
		{
			title: "Total line amount",
			dataIndex: "totalLineAmount",
			key: "totalLineAmount",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "totalLineAmount"),
			render: (_, record) => <span>{getCellValue(record, ["totalLineAmount"])}</span>,
		},
		{
			title: "Reason for reject",
			dataIndex: "reasonForRejection",
			key: "reasonForRejection",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "reasonForRejection"),
			render: (_, record) => <span>{getCellValue(record, ["reasonForRejection"])}</span>,
		},
		{
			title: "Onhand qty1",
			dataIndex: "onHandQty1",
			key: "onHandQty1",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "onHandQty1"),
			render: (_, record) => <span>{getCellValue(record, ["onHandQty1"])}</span>,
		},
		{
			title: "Unit 1",
			dataIndex: "unit1",
			key: "unit1",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 110,
			sortOrder: getSortOrder(tableSorter, "unit1"),
			render: (_, record) => <span>{getCellValue(record, ["unit1"])}</span>,
		},
		{
			title: "Onhand qty2",
			dataIndex: "onHandQty2",
			key: "onHandQty2",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "onHandQty2"),
			render: (_, record) => <span>{getCellValue(record, ["onHandQty2"])}</span>,
		},
		{
			title: "Unit 2",
			dataIndex: "unit2",
			key: "unit2",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 110,
			sortOrder: getSortOrder(tableSorter, "unit2"),
			render: (_, record) => <span>{getCellValue(record, ["unit2"])}</span>,
		},
		{
			title: "Status Reason",
			dataIndex: "statusReson",
			key: "statusReson",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "statusReson"),
			render: (_, record) => <span>{getCellValue(record, ["statusReson"])}</span>,
		},
	];

	return (
		<Fragment>
			<div className="dashboard app-content content kyc-order-details-page">
				<div className="content-wrapper">
					<div className="content-body">
						<KycStickyTopBar
							variant="details"
							className="kyc-dashboard-top-bar details_view_title kyc-details-top-bar">
							<div className="kyc-dashboard-top-bar__left">
								<div className="kyc-dashboard-top-bar__breadcrumb">
									<Breadcrumb className="details_breadcrum" separator={">"}>
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								{isPartnerReturn ? (
									<>
										{showPartnerGroupInBreadcrumb ? (
											<Breadcrumb.Item>
												<Link to={dashboardGroupCompanyLink}>
													<label>{groupCompanyBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										{showPartnerMaterialsInBreadcrumb ? (
											<Breadcrumb.Item>
												<Link to={dashboardMaterialsDrillAccountLink}>
													<label>{materialsBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										{showPartnerAccountInBreadcrumb && !groupAndAccountNamesMatch ? (
											<Breadcrumb.Item>
												<Link to={dashboardAccountLink}>
													<label>{accountBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										<Breadcrumb.Item>
											<Link
												to={partnerFunctionTabReturn.link}
												state={partnerFunctionTabReturn.state}>
												<label>Partner Function</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<Link to={dashboardOrdersBackLink} state={dashboardOrdersBackState}>
												<label>Orders</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{orderNumberLabel}</label>
										</Breadcrumb.Item>
									</>
								) : isMaterialReturn ? (
									<>
										<Breadcrumb.Item>
											<Link
												to={dashboardMaterialsReturn.link}
												state={dashboardMaterialsReturn.state}>
												<label>{materialBreadcrumbLabel}</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<Link to={dashboardOrdersBackLink} state={dashboardOrdersBackState}>
												<label>Orders</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{orderNumberLabel}</label>
										</Breadcrumb.Item>
									</>
								) : (
									<>
										<Breadcrumb.Item>
											<Link to={dashboardOrdersTabLink}>
												<label>Orders</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{orderNumberLabel}</label>
										</Breadcrumb.Item>
									</>
								)}
							</Breadcrumb>
								</div>
							</div>
							<div className="kyc-dashboard-top-bar__actions">
							<Link to={dashboardOrdersBackLink} state={dashboardOrdersBackState}>
								<Button type="primary" className="back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
							</div>
						</KycStickyTopBar>

						<Spin spinning={isLoading}>
							<Card className="kyc-table-card kyc-order-items-card">
								<h5 className="kyc-company-title mb-3">CRM Order</h5>
								<Table
									bordered
									scroll={{ x: 2200 }}
									className="logistic_tables kyc_logistic_tables kyc_order_items_table"
									dataSource={lineItems}
									columns={lineItemColumns}
									sortDirections={["ascend", "descend"]}
									onChange={handleTableAscDes}
									rowKey={(record, index) => {
										const itemNo = record?.itemNo ? String(record.itemNo) : "";
										const id = record?.orderId ? String(record.orderId) : "";
										if (itemNo && id) {
											return `${id}-${itemNo}`;
										}
										return itemNo || id || index;
									}}
									locale={{ emptyText: tableEmptyText }}
									pagination={{
										total: tableState.totalRecords,
										current: tableState.currentPage,
										pageSize: tableState.pageSize,
										responsive: true,
										showSizeChanger: false,
										showTotal: formatRecordCount,
									}}
								/>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycOrderDetails;
